import React, { useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Customer,
  CustomerContext,
  SubscriptionContext,
  useSubscription
} from 'ui-smartforce-settings';
import { OnlineStatusContext } from '../../../../../Context/OnlineStatusContext';
import { SFScrollableRefHandler } from 'sfui';
import {
  InitiativesDashboardResponse,
  InitiativesTotals,
  InitiativeValue
} from '../../../../../Models/InitiativeValue';
import { TinyWidgetTotalsProps } from '../../../../../Components/TinyWidgets/TinyWidgetsContent/TinyWidgetsContent';
import { handleError } from '../../../../../Helpers';
import {
  getNextInitiativesReports,
  getInitiativesReports,
  getTotals
} from '../../../../../Services/InitiativesService';
import {
  durationTimeFormatStyled,
  getWidgetOrder,
  getWidgetTitle
} from '../helpers';

export interface UseInitiativesDashboardProps {}

export interface UseInitiativesDashboardReturn {
  isLoading: boolean;
  isFetchingMoreInitiatives: boolean;
  refScrollable: React.RefObject<SFScrollableRefHandler>;
  refNext: React.RefObject<string | undefined>;
  initiatives: InitiativeValue[];
  totals: InitiativesTotals | undefined;
  tinyWidgetsTotals: TinyWidgetTotalsProps[];
  onScrollBottom: () => void;
}

export const useInitiativesDashboard = (
  props: UseInitiativesDashboardProps
): UseInitiativesDashboardReturn => {
  const history = useHistory();
  const location = useLocation();
  const timezone = (useContext(CustomerContext).customer as Customer).timezone;
  const { isOnline } = useContext(OnlineStatusContext);
  const { subscriptionsSummary } = useContext(SubscriptionContext);
  const subscription = useSubscription('cc');

  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isFetchingMoreInitiatives, setIsFetchingMoreInitiatives] =
    React.useState<boolean>(true);

  const refNext = React.useRef<string | undefined>();
  const refScrollable = React.useRef<SFScrollableRefHandler>(null);

  const [initiatives, setInitiatives] = React.useState<InitiativeValue[]>([]);
  const [totals, setTotals] = React.useState<InitiativesTotals | undefined>();

  React.useEffect(() => {
    let isSubscribed: boolean = true;

    const getInitiatives = async () => {
      try {
        setIsLoading(true);

        const response: InitiativesDashboardResponse =
          await getInitiativesReports();
        const totalsResponse: InitiativesTotals = await getTotals();

        if (isSubscribed) {
          refNext.current = response.links.next;
          setInitiatives(response.initiatives);
          setTotals(totalsResponse);
          setIsLoading(false);
          setIsFetchingMoreInitiatives(false);
        }
      } catch (e) {
        console.error('InitiativesDashboard::getInitiativesReports', e);
        handleError(e, history);
      }
    };

    if (isOnline) getInitiatives();

    return () => {
      isSubscribed = false;
    };
  }, [
    history,
    location.search,
    isOnline,
    timezone,
    subscriptionsSummary,
    subscription
  ]);

  const onScrollBottom = async () => {
    if (!!refNext && !!refNext.current && !isFetchingMoreInitiatives) {
      setIsFetchingMoreInitiatives(true);
      try {
        const response = await getNextInitiativesReports(refNext.current);
        setInitiatives([...initiatives, ...response.initiatives]);
        setIsFetchingMoreInitiatives(false);
        refNext.current = response.links.next;
      } catch (e) {
        console.error('InitiativesDashboard::getMoreReports', e);
        handleError(e, history);
      }
    }
  };

  // Prepare totals array for TinyWidgets component
  const tinyWidgetsTotals: TinyWidgetTotalsProps[] = React.useMemo(() => {
    const totalsMap: TinyWidgetTotalsProps[] = [];
    Object.keys(totals || {}).forEach((key) => {
      totalsMap.push({
        title: getWidgetTitle(key),
        value: totals![key],
        order: getWidgetOrder(key),
        getCustomFormattedValue:
          key === 'time_spent' ? durationTimeFormatStyled : undefined
      });
    });
    return totalsMap.sort((a, b) => {
      if (a.order && b.order) return a.order - b.order;
      return -1;
    });
  }, [totals]);

  return {
    isLoading,
    isFetchingMoreInitiatives,
    refScrollable,
    refNext,
    initiatives,
    totals,
    tinyWidgetsTotals,
    onScrollBottom
  };
};
